import React from 'react';
import { Domain, SpoofornotDetail } from '../../../types/interfaces';
import { useTranslation } from 'react-i18next';

interface SpoofOrNotProps {
  selectedDomain: string;
  domains: Domain[];
  onDomainChange: (domain: string) => void;
  spoofornotData?: SpoofornotDetail;
}

const SpoofOrNot: React.FC<SpoofOrNotProps> = ({
  selectedDomain,
  domains,
  onDomainChange,
  spoofornotData,
}) => {
  const { t } = useTranslation();

  // Manejador para cambiar de dominio
  const handleDomainChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onDomainChange(e.target.value);
  };

  // Función para generar la explicación según el valor de spoofing_possible
  /*
  const getSuplantacionPossibleExplanation = (spoofingPossible: string): string => {
    switch (spoofingPossible) {
      case '0':
        return t('spoofOrNot.explanation.0');
      case '1':
        return t('spoofOrNot.explanation.1');
      case '2':
        return t('spoofOrNot.explanation.2');
      case '3':
        return t('spoofOrNot.explanation.3');
      case '4':
        return t('spoofOrNot.explanation.4');
      case '5':
        return t('spoofOrNot.explanation.5');
      case '6':
        return t('spoofOrNot.explanation.6');
      case '7':
        return t('spoofOrNot.explanation.7');
      default:
        return t('spoofOrNot.explanation.unknown');
    }
  };
  */

  const getSuplantacionPossibleExplanation = (
    spoofingPossible: string | null
  ): string => {
    return spoofingPossible === 'false'
      ? t('spoofOrNot.explanation.7')
      : t('spoofOrNot.explanation.0');
  };


  const getIconAndColor = (spoofingPossible: string | null) => {
    if (spoofingPossible === 'false') {
      return { colorClass: 'text-green-500', icon: '✅' };
    }
    return { colorClass: 'text-yellow-500', icon: '⚠️' };
  };

  return (
    <div
      className="p-8 bg-white rounded-lg shadow-lg mb-6"
      style={{ boxShadow: '0px 4px 10px rgba(169, 169, 169, 0.6)' }}
    >
      {/* Encabezado */}
      <div className="flex items-center mb-6">
        <div className="w-3 h-10 bg-gray-800 mr-4"></div>
        <h3 className="text-4xl font-bold">{t('spoofOrNot.title')}</h3>
      </div>

      {/* Descripción */}
      <p className="text-gray-700 mb-4 text-xl">
        {t('spoofOrNot.paragraph1')}
      </p>
      <p className="text-gray-700 mb-4 text-xl">
        {t('spoofOrNot.paragraph2')}
      </p>
      <p className="text-gray-700 text-xl">
        {t('spoofOrNot.paragraph3')}
      </p>

      {/* Selección de dominio */}
      <div className="mb-4">
        <label htmlFor="domain-select" className="block mt-8 mb-2 text-lg font-medium">
          <strong>{t('spoofOrNot.select_domain')}</strong>
        </label>
        <select
          id="domain-select"
          value={selectedDomain}
          onChange={handleDomainChange}
          className="p-2 border rounded bg-gray-800 text-white font-bold w-64"
        >
          {domains.map((d) => (
            <option key={d.id} value={d.domain_name}>
              {d.domain_name}
            </option>
          ))}
        </select>
      </div>

      {/* Información de Suplantación */}
      <div className="relative overflow-x-auto bg-white p-4 rounded-lg min-h-[200px]">
        {spoofornotData ? (
          <>
            <table className="min-w-full text-left bg-white border rounded-lg">
              <thead className="bg-gray-800 text-white">
                <tr>
                  <th className="py-3 px-6 text-sm font-semibold tracking-wider text-center">
                    {t('spoofOrNot.table.field')}
                  </th>
                  <th className="py-3 px-6 text-sm font-semibold tracking-wider text-center">
                    {t('spoofOrNot.table.value')}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr className="text-center bg-gray-50">
                  <td className="py-3 px-6 border">SPF</td>
                  <td className="py-3 px-6 border">{spoofornotData.spf || t('spoofOrNot.not_found')}</td>
                </tr>
                <tr className="text-center bg-gray-50">
                  <td className="py-3 px-6 border">DMARC</td>
                  <td className="py-3 px-6 border">{spoofornotData.dmarc || t('spoofOrNot.not_found')}</td>
                </tr>
              </tbody>
            </table>

            {/* Sección de explicación adicional */}
            <div className="mt-6 flex flex-col items-center">
              {spoofornotData.spoofing_possible !== undefined ? (
                <>
                  <div className="mb-4">
                    {(() => {
                      const { colorClass, icon } = getIconAndColor(spoofornotData.spoofing_possible);
                      return <span className={`${colorClass} text-6xl`}>{icon}</span>;
                    })()}
                  </div>
                  <p className="text-gray-700 text-xl text-center">
                    {getSuplantacionPossibleExplanation(spoofornotData.spoofing_possible)}
                  </p>
                </>
              ) : (
                <p className="text-gray-700 text-xl">
                  {t('spoofOrNot.no_spoofing_determined')}
                </p>
              )}
            </div>
          </>
        ) : (
          <p className="text-center">{t('spoofOrNot.no_data')}</p>
        )}
      </div>
    </div>
  );
};

export default SpoofOrNot;
